/**
 * CaseStudiesPanel — admin CMS for case studies (add / edit / publish /
 * reorder / delete). Edits go live immediately (no redeploy). List fields are
 * edited one-item-per-line. Mirrors the testimonials CMS pattern.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Eye, EyeOff, ChevronUp, ChevronDown, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

const linesToArr = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const arrToLines = (a) => (a || []).join("\n");

const BLANK = {
  title: "", sector: "", metric_value: "", metric_label: "", services_tags: [],
  business_situation: "", why_complex: [], what_changed: "", impact: "",
  delivery_moves: [], evidence: [], what_this_proves: "", relevant_services: [],
  published: false, order: 0,
};

const fieldCls = "w-full border border-zinc-300 px-2.5 py-1.5 text-[13px]";
const lbl = "text-[11px] font-medium text-zinc-600";

function Editor({ initial, onSave, onCancel, saving }) {
  const [f, setF] = useState({
    ...BLANK, ...initial,
    services_tags_txt: arrToLines(initial?.services_tags),
    why_complex_txt: arrToLines(initial?.why_complex),
    delivery_moves_txt: arrToLines(initial?.delivery_moves),
    evidence_txt: arrToLines(initial?.evidence),
    relevant_services_txt: arrToLines(initial?.relevant_services),
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = () => {
    if (!f.title.trim()) { toast.error("Title is required"); return; }
    onSave({
      title: f.title, sector: f.sector, metric_value: f.metric_value, metric_label: f.metric_label,
      services_tags: linesToArr(f.services_tags_txt), business_situation: f.business_situation,
      why_complex: linesToArr(f.why_complex_txt), what_changed: f.what_changed, impact: f.impact,
      delivery_moves: linesToArr(f.delivery_moves_txt), evidence: linesToArr(f.evidence_txt),
      what_this_proves: f.what_this_proves, relevant_services: linesToArr(f.relevant_services_txt),
      published: !!f.published, order: f.order || 0,
    });
  };

  return (
    <div className="border border-zinc-300 bg-zinc-50 p-4 space-y-3" data-testid="case-study-editor">
      <div><label className={lbl}>Title</label><input value={f.title} onChange={(e) => set("title", e.target.value)} className={fieldCls} data-testid="cs-edit-title" /></div>
      <div><label className={lbl}>Sector</label><input value={f.sector} onChange={(e) => set("sector", e.target.value)} className={fieldCls} data-testid="cs-edit-sector" /></div>
      <div className="grid grid-cols-2 gap-2">
        <div><label className={lbl}>Metric value (e.g. 99%, £150m+, Stronger)</label><input value={f.metric_value} onChange={(e) => set("metric_value", e.target.value)} className={fieldCls} data-testid="cs-edit-metric-value" /></div>
        <div><label className={lbl}>Metric label</label><input value={f.metric_label} onChange={(e) => set("metric_label", e.target.value)} className={fieldCls} data-testid="cs-edit-metric-label" /></div>
      </div>
      <div><label className={lbl}>Service tags (one per line)</label><textarea rows={3} value={f.services_tags_txt} onChange={(e) => set("services_tags_txt", e.target.value)} className={fieldCls} data-testid="cs-edit-tags" /></div>
      <div><label className={lbl}>The business situation</label><textarea rows={3} value={f.business_situation} onChange={(e) => set("business_situation", e.target.value)} className={fieldCls} data-testid="cs-edit-situation" /></div>
      <div><label className={lbl}>Why it was complex (one per line)</label><textarea rows={4} value={f.why_complex_txt} onChange={(e) => set("why_complex_txt", e.target.value)} className={fieldCls} data-testid="cs-edit-why" /></div>
      <div><label className={lbl}>What changed</label><textarea rows={3} value={f.what_changed} onChange={(e) => set("what_changed", e.target.value)} className={fieldCls} data-testid="cs-edit-changed" /></div>
      <div><label className={lbl}>Commercial and operational impact</label><textarea rows={3} value={f.impact} onChange={(e) => set("impact", e.target.value)} className={fieldCls} data-testid="cs-edit-impact" /></div>
      <div><label className={lbl}>Delivery moves (one per line)</label><textarea rows={4} value={f.delivery_moves_txt} onChange={(e) => set("delivery_moves_txt", e.target.value)} className={fieldCls} data-testid="cs-edit-moves" /></div>
      <div><label className={lbl}>Evidence of value (one per line)</label><textarea rows={4} value={f.evidence_txt} onChange={(e) => set("evidence_txt", e.target.value)} className={fieldCls} data-testid="cs-edit-evidence" /></div>
      <div><label className={lbl}>What this proves</label><textarea rows={2} value={f.what_this_proves} onChange={(e) => set("what_this_proves", e.target.value)} className={fieldCls} data-testid="cs-edit-proves" /></div>
      <div><label className={lbl}>Relevant Ekselens services (one per line)</label><textarea rows={4} value={f.relevant_services_txt} onChange={(e) => set("relevant_services_txt", e.target.value)} className={fieldCls} data-testid="cs-edit-services" /></div>
      <label className="flex items-center gap-2 text-[13px] text-zinc-700"><input type="checkbox" checked={!!f.published} onChange={(e) => set("published", e.target.checked)} data-testid="cs-edit-published" /> Published (visible on the public site)</label>
      <div className="flex gap-2">
        <button onClick={submit} disabled={saving} className="ek-btn ek-btn-primary text-[13px]" data-testid="cs-edit-save">{saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save</button>
        <button onClick={onCancel} className="ek-btn text-[13px]" data-testid="cs-edit-cancel">Cancel</button>
      </div>
    </div>
  );
}

export default function CaseStudiesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // id | "new"
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/content/case-studies"); setRows(data.case_studies || []); }
    catch { toast.error("Failed to load case studies"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (payload) => {
    setSaving(true);
    try { await api.post("/admin/content/case-studies", payload); toast.success("Case study created"); setEditing(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };
  const update = async (id, payload) => {
    setSaving(true);
    try { await api.put(`/admin/content/case-studies/${id}`, payload); toast.success("Saved — live now"); setEditing(null); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const togglePublish = async (cs) => {
    try { await api.put(`/admin/content/case-studies/${cs.id}`, { ...cs, published: !cs.published }); await load(); }
    catch { toast.error("Could not update"); }
  };
  const remove = async (cs) => {
    if (!window.confirm(`Delete "${cs.title}"?`)) return;
    try { await api.delete(`/admin/content/case-studies/${cs.id}`); toast.success("Deleted"); await load(); }
    catch { toast.error("Delete failed"); }
  };
  const move = async (idx, dir) => {
    const next = [...rows];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setRows(next);
    try { await api.put("/admin/content/case-studies-order", { ids: next.map((r) => r.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };

  if (loading) return <div className="text-[13px] text-zinc-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="case-studies-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="ek-heading text-lg">Case studies</h3>
          <p className="text-[12px] text-zinc-500">Add, edit, publish and reorder — changes go live immediately at <code>/case-studies</code>.</p>
        </div>
        <button onClick={() => setEditing("new")} className="ek-btn ek-btn-primary text-[13px]" data-testid="cs-add-btn"><Plus size={14} /> Add case study</button>
      </div>

      {editing === "new" && <Editor initial={BLANK} onSave={create} onCancel={() => setEditing(null)} saving={saving} />}

      <div className="space-y-3">
        {rows.map((cs, idx) => (
          <div key={cs.id} className="border border-zinc-200 bg-white" data-testid={`cs-row-${cs.slug}`}>
            <div className="flex items-center gap-3 p-3">
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-zinc-400 hover:text-black disabled:opacity-30" data-testid={`cs-up-${cs.slug}`}><ChevronUp size={16} /></button>
                <button onClick={() => move(idx, 1)} disabled={idx === rows.length - 1} className="text-zinc-400 hover:text-black disabled:opacity-30" data-testid={`cs-down-${cs.slug}`}><ChevronDown size={16} /></button>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-black truncate">{cs.title}</div>
                <div className="text-[11px] text-zinc-500">{cs.sector} · <span className="font-medium">{cs.metric_value}</span> {cs.metric_label}</div>
              </div>
              <span className={`text-[10.5px] font-medium px-2 py-0.5 ${cs.published ? "bg-emerald-50 text-emerald-700" : "bg-zinc-100 text-zinc-500"}`}>{cs.published ? "Published" : "Draft"}</span>
              <button onClick={() => togglePublish(cs)} className="text-zinc-500 hover:text-black" title={cs.published ? "Unpublish" : "Publish"} data-testid={`cs-toggle-${cs.slug}`}>{cs.published ? <EyeOff size={16} /> : <Eye size={16} />}</button>
              <button onClick={() => setEditing(editing === cs.id ? null : cs.id)} className="ek-btn text-[12px]" data-testid={`cs-edit-${cs.slug}`}>Edit</button>
              <button onClick={() => remove(cs)} className="text-zinc-400 hover:text-red-600" data-testid={`cs-delete-${cs.slug}`}><Trash2 size={15} /></button>
            </div>
            {editing === cs.id && (
              <div className="p-3 border-t border-zinc-100">
                <Editor initial={cs} onSave={(p) => update(cs.id, p)} onCancel={() => setEditing(null)} saving={saving} />
              </div>
            )}
          </div>
        ))}
        {rows.length === 0 && <p className="text-[13px] text-zinc-500">No case studies yet — add one above.</p>}
      </div>
    </div>
  );
}
